import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import styled from 'styled-components';
import { theme } from '../../../../styles/theme';
import { IRichTextEditorRef } from '../../../RichTextEditor/RichTextEditor';
import Note from '.';
import { ContentSplitResult } from '../useContentSplit';
import { IScratchPadNoteFragment } from '../../types';

const DragHandle = styled.div`
  cursor: grab;
  padding: 8px;
  display: flex;
  align-items: center;
  color: ${theme.colors.secondary600};
  opacity: 0;
  transition: opacity 0.2s;

  &:hover {
    color: ${theme.colors.secondary600};
  }
`;

const NoteWrapper = styled.div<{ isDragging?: boolean }>`
  display: flex;
  align-items: flex-start;
  opacity: ${(props) => (props.isDragging ? 0.5 : 1)};

  &:hover ${DragHandle} {
    opacity: 1;
  }
`;

interface SortableNoteProps {
  note: IScratchPadNoteFragment;
  currentNoteId: string | null;
  editorRefs: React.MutableRefObject<{ [key: string]: IRichTextEditorRef }>;
  handleContentChange: (content: string, noteId?: string) => Promise<void>;
  handleAddContent: (type: string, split?: ContentSplitResult) => Promise<void>;
  handleFocus: (noteId: string | null) => void;
}

const SortableNote = ({
  note,
  currentNoteId,
  editorRefs,
  handleContentChange,
  handleAddContent,
  handleFocus,
}: SortableNoteProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: note.id,
    animateLayoutChanges: ({ wasDragging }) => wasDragging,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <NoteWrapper ref={setNodeRef} style={style} isDragging={isDragging}>
      <Note
        currentNoteId={currentNoteId}
        editorRefs={editorRefs}
        handleContentChange={handleContentChange}
        handleAddContent={handleAddContent}
        handleFocus={handleFocus}
        note={note}
        dragHandleProps={{ attributes, listeners }}
      />
    </NoteWrapper>
  );
};

export default SortableNote;
